import { QueryCache, QueryClient } from '@tanstack/react-query';
import Axios from 'axios';
import { toast } from 'sonner';

import { SensorTypeCode } from '@/common/asset-sensor';
import { t } from '@/i18n';
import { apiErrorMessage, extractApiError } from './common';

export const queryClient = new QueryClient({
	queryCache: new QueryCache({
		onSuccess(data, query) {
			if (data && typeof data === 'object' && 'error' in data && data.error) {
				const error = (data as { error: unknown }).error;
				console.error(`Query ${String(query.queryKey)} returned error:`, error);
				// Never pass a non-string to toast — rendering an object as a React child
				// crashes the Toaster and takes the whole app down with it.
				toast.error(typeof error === 'string' ? error : t('common.errors.unexpected'));
			}
		},
		onError(error, query) {
			// A cancelled request (e.g. no token, so the app is redirecting to login) is
			// not a failure the user needs to hear about.
			if (Axios.isCancel(error)) return;
			const { statusCode, requestId } = extractApiError(error);
			console.error(`Query ${String(query.queryKey)} failed — requestId: ${requestId ?? 'n/a'}`, error);
			// Queries that show their own error inline (e.g. the delete/restore previews) opt out of the toast
			if (query.meta?.skipGlobalErrorToast) return;
			// 401 triggers a login redirect in the Axios interceptor — don't toast over it.
			// Server-side errors are captured to Sentry by the backend GlobalExceptionFilter,
			// so we don't double-report them from the browser here.
			if (statusCode !== 401) {
				toast.error(apiErrorMessage(error));
			}
		},
	}),
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false, // default: true
			retry: false, // default: 3
		},
	},
});

export const API_URLS = {
	AUTH: {
		LOGIN: `/auth/login`,
	},
	USERS: {
		INDEX: `/users`,
		PROFILE: `/users/profile`,
		TEMP_PASSWORD: (userId: string) => `/users/${userId}/temp-password`,
		SEND_PASSWORD_RESET_EMAIL: (userId: string) => `/users/${userId}/send-password-reset-email`,
	},
	ASSETS: {
		INDEX: `/assets`,
		USERS: (assetId: string) => `/assets/${assetId}/users`,
		SENSORS: {
			INDEX: (assetId: string) => `/assets/${assetId}/sensors`,
			TPS: {
				PERF_STATS: (assetId: string) => `/assets/${assetId}/sensors/${SensorTypeCode.TPS}/perfstats`,
				TIMESERIES: (assetId: string, dataLoggerId: number, sensorId: number) =>
					`/assets/${assetId}/sensors/${SensorTypeCode.TPS}/${dataLoggerId}/${sensorId}/timeseries`,
				SECTION_CHART: (assetId: string) => `/assets/${assetId}/sensors/${SensorTypeCode.TPS}/section`,
			},
			MET: {
				TIMESERIES: (assetId: string, sensorId: number) =>
					`/assets/${assetId}/sensors/${SensorTypeCode.MET}/${sensorId}/timeseries`,
			},
		},
	},
	APP_CONFIG: {
		DROPDOWNS: `/appconfig/dropdowns`,
	},
	SUB_ASSETS: {
		INDEX: `/sub-assets`,
	},
	INSPECTION: {
		INDEX: (assetId: string) => `/assets/${assetId}/inspections`,
	},
	DAMAGES: {
		INDEX: `/damages`,
	},
	DAMAGE_POLYGONS: {
		INDEX: `/polygons`,
	},
	FILE_UPLOAD: {
		PRESIGNED_URL: '/file-upload/presigned-url',
	},
	DATA_UPLOAD: {
		INDEX: `/data-uploads`,
		PRESIGNED_URL: (id: string) => `/data-uploads/${id}/presigned-url`,
		TILESET_JOB: `/data-uploads/tileset/job`,
		TILESET_CAPABILITIES: `/data-uploads/tileset/capabilities`,
		TILESET_ACTIVE: `/data-uploads/tileset/active`,
		TILESET_STATUS: (id: string) => `/data-uploads/tileset/${id}/status`,
		TILESET_LOG: (id: string) => `/data-uploads/tileset/${id}/log`,
		TILESET_LOCATION: (id: string) => `/data-uploads/tileset/${id}/location`,
		TILESET_REPROCESS: (id: string) => `/data-uploads/tileset/${id}/reprocess`,
	},
	WORK_PACKAGES: {
		INDEX: `/work-packages`,
	},
	ORGANIZATIONS: {
		INDEX: `/organizations`,
		MEMBERS: (organizationId: string) => `/organizations/${organizationId}/members`,
	},
	PERMISSIONS: {
		ROLES: `/permissions/roles`,
	},
};
